import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert, EmptyState, Badge } from '../../components/UI';

function ringColor(pct) {
  if (pct >= 75) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
}

function formatDate(ts) {
  if (!ts) return '-';
  return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function UserActivityDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/reports/user-activity/${userId}`).then(setData).catch(e => setError(e.message));
  }, [userId]);

  if (!data && !error) return <Spinner />;
  if (error && !data) return <div className="content-narrow"><Alert type="danger">{error}</Alert></div>;

  const { user, attempts } = data;
  const list = attempts || [];
  // An attempt is "scored" once it has been evaluated (max_score set); admins see
  // the score even before the result is published to the candidate.
  const scored = a => a.max_score > 0 && a.total_score != null;
  const publishedCount = list.filter(r => r.is_published).length;

  return (
    <div className="content-narrow">
      <div className="page-header">
        <div>
          <div className="eyebrow"><Link to="/admin/user-activity">&larr; User Activity</Link></div>
          <h1>{user.name}</h1>
          <p className="mono text-sm">{user.mobile}</p>
        </div>
      </div>

      <Alert type="danger">{error}</Alert>

      {list.length === 0 && !error && (
        <div className="card">
          <EmptyState title="No attempts yet" sub="This user has not submitted any exams." />
        </div>
      )}

      {list.length > 0 && (
        <>
          <div className="results-summary">
            <div className="rs-item"><span className="rs-num">{list.length}</span><span className="rs-label">Exams taken</span></div>
            <div className="rs-item"><span className="rs-num">{publishedCount}</span><span className="rs-label">Published</span></div>
            <div className="rs-item"><span className="rs-num">{list.length - publishedCount}</span><span className="rs-label">Not published</span></div>
          </div>

          <div className="result-list">
            {list.map(r => {
              const pct = scored(r) ? Math.round((r.total_score / r.max_score) * 100) : null;
              return (
                <div className="result-card" key={r.id}>
                  <div className="result-info">
                    {/* The exam name itself drills into the full breakdown. */}
                    <Link to={`/admin/user-activity/${userId}/attempts/${r.id}`} className="result-title result-title-link">
                      {r.paper_title}
                    </Link>
                    <div className="result-meta">Submitted {formatDate(r.submitted_at)}</div>
                  </div>

                  {scored(r) ? (
                    <div className="result-score">
                      <div className="score-side">
                        <div className="score-fraction">{r.total_score} / {r.max_score}</div>
                        <Link to={`/admin/user-activity/${userId}/attempts/${r.id}`} className="btn btn-outline btn-sm">View details</Link>
                      </div>
                      <div className="score-ring" style={{ '--pct': pct, '--ring': ringColor(pct) }}>
                        <span>{pct}%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="result-pending">
                      <Badge tone="warning">Awaiting evaluation</Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
